import type { AvatarKind } from '../../../components/ui/Avatar.js';
import type { EmployeeStatus } from './status.js';

// QG-CLI-COMP-011: the duplicated workforce Avatar.tsx (and its EmployeeKind
// type) is gone — components/ui/Avatar.tsx is the single implementation now.
export type EmployeeKind = AvatarKind;
import { mapRuntimeStatus } from './status.js';
import {
  type Assignment,
  type Attention,
  type CurrentFocus,
  type Lifecycle,
  type Load,
  type LoadState,
  deriveLifecycle,
  deriveLoadState,
} from './bench-model.js';

/**
 * The workforce/employees endpoint returns `WorkforceRecord` which is opaque
 * (`{ [key: string]: unknown }`). The designer mocks reference a richer field
 * set than the API currently exposes. This adapter normalises the raw record
 * into the designer shape, filling missing fields with safe defaults so the
 * UI renders without per-field guards.
 *
 * Fields not yet supplied by the backend (e.g. `currentChange`, `goals`,
 * `blockerLong`, perf metrics) come back as `null`/`undefined`/`0`. UI panels
 * MUST handle empty data gracefully — this is the source of truth for what
 * the workforce surface believes about an employee.
 */

const STATUS_KEYS = ['working', 'available', 'review', 'blocked', 'paused', 'setup', 'terminated'] as const;

const VALID_LOAD_STATES: ReadonlySet<string> = new Set([
  'assigned',
  'unassigned',
  'at_capacity',
  'over_capacity',
  'frozen',
]);

export interface EmployeeView {
  id: string;
  name: string;
  kind: EmployeeKind;
  initials?: string;
  role: string;
  tier: string;
  skills: string[];

  // ── Global Bench five-axis model (org-wide). See bench-model.ts. ──
  lifecycle: Lifecycle;
  load: Load;
  attention: Attention;
  assignments: Assignment[];
  currentFocus: CurrentFocus;

  // ── Project-local status (correct only inside a project). ──
  status: EmployeeStatus;
  statusLabel?: string;
  blockerShort: string | null;
  blockerLong: string | null;
  attn: boolean;

  project: string | null;
  team: string | null;
  manager: string | null;
  workstream: string | null;

  currentChange: string | null;
  currentStep: string;
  lastActivity: string;

  tasksAssigned: number;
  tasksDone: number;
  tasksAwaitingReview: number;
  artifactsProduced: number;
  decisionsPending: number;

  reliability: number;
  passRate: number;
  latencyMs: number;
  cost7d: number;
  cost7dTrend: number;

  mandate: string;
  goals: string[];
  reportingRules: string;

  // Raw record kept for fall-through reads in panels that need server-only
  // fields the view doesn't yet model.
  raw: Record<string, unknown>;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

function arr(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return [];
}

function relativeFromIso(iso: unknown): string {
  if (typeof iso !== 'string') return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';
  const delta = Date.now() - ts;
  if (delta < 60_000) return 'now';
  if (delta < 3_600_000) return `${Math.round(delta / 60_000)}m`;
  if (delta < 86_400_000) return `${Math.round(delta / 3_600_000)}h`;
  const days = Math.round(delta / 86_400_000);
  return days === 1 ? 'yesterday' : `${days}d`;
}

export function toEmployeeView(raw: Record<string, unknown>): EmployeeView {
  const name = str(raw.name, 'Unnamed');
  // The runtime type discriminator on AI employee rows is `type`. Employee
  // types such as guardian / sentinel / operative are all AI. Human
  // collaborators land via member_type='user'; v1 has no humans in the
  // employee endpoint yet, so we default to 'ai'. When humans arrive,
  // raw.kind === 'human' selects.
  const explicitKind = raw.kind === 'human' ? 'human' : raw.kind === 'ai' ? 'ai' : null;
  const kind: EmployeeKind = explicitKind ?? 'ai';

  const department = str(raw.department, 'custom');
  const role = str(
    raw.role,
    department === 'custom' ? 'Workforce member' : `${department[0].toUpperCase()}${department.slice(1)}`,
  );

  const permissionTier = str(raw.permissionTier, 'observer');
  const tier =
    str(raw.tier) ||
    `${permissionTier === 'operator' ? 'T3' : permissionTier === 'contributor' ? 'T2' : 'T1'} · ${permissionTier}`;

  const blockerShort = strOrNull(raw.blockerShort);
  const blockerLong = strOrNull(raw.blockerLong);
  const explicitStatus = typeof raw.status === 'string' ? (raw.status as string) : null;
  const status: EmployeeStatus = (() => {
    if (explicitStatus && (STATUS_KEYS as readonly string[]).includes(explicitStatus)) {
      return explicitStatus as EmployeeStatus;
    }
    return mapRuntimeStatus(explicitStatus, Boolean(blockerShort));
  })();

  const reliability = num(raw.reliability ?? raw.qualityScore, 0);
  const passRate = num(raw.passRate ?? raw.qualityScore, 0);

  // ── Bench axes derived from the real record. Assignment-dependent axes
  // (assignments[], currentFocus, project-traced attention) start empty and
  // are merged in by `applyAssignmentsToView` from the real team-membership
  // feed. Load is server-authoritative when the daemon LIST endpoint folds
  // loadState/activeAssignments/capacity/headroom onto the row; otherwise
  // fall back to "unassigned / 0 active" so the view renders safely. ──
  const lifecycle = deriveLifecycle(raw);
  const skills = arr(raw.skills).length ? arr(raw.skills) : arr(raw.capabilities ?? raw.tags);

  const serverLoadState =
    typeof raw.loadState === 'string' && VALID_LOAD_STATES.has(raw.loadState) ? (raw.loadState as LoadState) : null;
  const serverLoad: Load =
    serverLoadState !== null
      ? {
          activeAssignments: num(raw.activeAssignments, 0),
          state: serverLoadState,
          capacity: raw.capacity != null ? (num(raw.capacity, 0) as number) : null,
          headroom: raw.headroom != null ? (num(raw.headroom, 0) as number) : null,
        }
      : { activeAssignments: 0, state: deriveLoadState(lifecycle, 0) };

  return {
    id: String(raw.id ?? ''),
    name,
    kind,
    initials: str(raw.avatar) || undefined,
    role,
    tier,
    skills,

    lifecycle,
    load: serverLoad,
    attention: { level: 'clear', detail: null, sourceProject: null },
    assignments: [],
    currentFocus: { label: null, projectId: null },

    status,
    statusLabel: strOrNull(raw.statusLabel) ?? undefined,
    blockerShort,
    blockerLong,
    attn: Boolean(raw.attn) || status === 'blocked' || status === 'setup' || status === 'review',

    project: strOrNull(raw.project),
    team: strOrNull(raw.team),
    manager: strOrNull(raw.manager),
    workstream: strOrNull(raw.workstream),

    currentChange: strOrNull(raw.currentChange),
    currentStep: str(raw.currentStep, status === 'available' ? 'idle — queue empty' : ''),
    lastActivity: str(raw.lastActivity) || relativeFromIso(raw.lastRunAt),

    tasksAssigned: num(raw.tasksAssigned),
    tasksDone: num(raw.tasksDone ?? raw.runCount),
    tasksAwaitingReview: num(raw.tasksAwaitingReview),
    artifactsProduced: num(raw.artifactsProduced),
    decisionsPending: num(raw.decisionsPending),

    reliability,
    passRate,
    latencyMs: num(raw.latencyMs ?? raw.lastRunDurationMs),
    cost7d: num(raw.cost7d),
    cost7dTrend: num(raw.cost7dTrend),

    mandate: str(raw.mandate),
    goals: arr(raw.goals),
    reportingRules: str(raw.reportingRules),

    raw,
  };
}

/**
 * Merge the real team-membership-derived project assignments into an employee
 * view. Returns a new view; the input is not mutated.
 *
 * Reconciliation strategy (DESIGN PRINCIPLE):
 * - assignments[], currentFocus, and project-traced attention come exclusively
 *   from the feed — only the feed knows per-project membership detail.
 * - load.state / load.activeAssignments / load.capacity / load.headroom are
 *   SERVER-AUTHORITATIVE: when the daemon list endpoint populated loadState
 *   (detectable because view.raw.loadState is a valid LoadState string), we
 *   keep the server values and do NOT override them from the feed-derived
 *   count. The server knows capacity; the client does not.
 * - Fallback: when the server did NOT supply loadState (e.g. older response or
 *   future dev-mode stub), we derive load from the feed's active-assignment
 *   count exactly as before, so the view degrades gracefully.
 */
export function applyAssignmentsToView(view: EmployeeView, assignments: Assignment[]): EmployeeView {
  const activeAssignments = assignments.filter((a) => a.active).length;
  const firstActive = assignments.find((a) => a.active) ?? null;

  const serverHasLoad = typeof view.raw.loadState === 'string' && VALID_LOAD_STATES.has(view.raw.loadState);

  const load: Load = serverHasLoad
    ? // Server load is authoritative — preserve state/capacity/headroom.
      // Only activeAssignments can be superseded by the feed if the feed has
      // more complete membership data; in practice the server count wins here
      // too (feed may lag), so we keep view.load.activeAssignments as well.
      view.load
    : // No server load available — derive from feed count (legacy path).
      {
        activeAssignments,
        state: deriveLoadState(view.lifecycle, activeAssignments),
      };

  return {
    ...view,
    assignments,
    load,
    currentFocus:
      view.currentFocus.label || !firstActive
        ? view.currentFocus
        : { label: firstActive.team ?? null, projectId: firstActive.projectId, projectName: firstActive.projectName },
  };
}
